import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import TripRequestsView from './companies-view';
import { useCompaniesStore } from '../oryx-state';
import {
  applyClientSideCompanyFilters,
  createCompanySearch,
  fetchCompanySearch,
  defaultSearch,
  getCompanySortValue,
  getCompanySortOrders,
  hasClientSideCompanyFilters,
  // TODO(activity-level-removal): drop this import along with the helper itself in the cleanup PR.
  legacyActivityLevelsToStatuses,
} from '../utils/companies';
import FetchStatus from '../utils/fetch-status';
import { extractParams } from '../../../utils/utils';
import useInfiniteScroll from '../../../hooks/use-infinite-scroll';

const CompaniesContainer = () => {
  const history = useHistory();
  const location = useLocation();

  const query = extractParams(location.search);

  const {
    filters,
    sortOrder,
    searchResults,
    searchResultsById,
    setCompanySearchResults,
    setCompanyFilters,
    setSortOrder,
  } = useCompaniesStore();

  const [searchTerm, setSearchTerm] = useState('');
  const [loadStatus, setLoadStatus] = useState(FetchStatus.IDLE);
  const [loadMoreStatus, setLoadMoreStatus] = useState(FetchStatus.IDLE);
  const loading = loadStatus === FetchStatus.FETCHING || loadMoreStatus === FetchStatus.FETCHING;
  const shouldSearch = useRef(false);
  const searchEpoch = useRef(0);
  const rawCompaniesRef = useRef([]);
  const lastServerSignatureRef = useRef(null);

  const buildServerSignature = (f, term, sort) => JSON.stringify({
    published: f?.published ?? null,
    primaryLocations: [...(f?.primaryLocations ?? [])].sort(),
    secondaryLocations: [...(f?.secondaryLocations ?? [])].sort(),
    statuses: [...(f?.statuses ?? [])].sort(),
    term: (term ?? '').trim(),
    sort,
  });

  const withClientSideFilters = (res) => ({
    ...res,
    loadedCount: res.companies?.length ?? 0,
    companies: applyClientSideCompanyFilters(res.companies, filters),
  });

  // Primary useEffect for search on filter, sort, or text change.
  useEffect(() => {
    if (shouldSearch.current) {
      // Sets shouldSearch.current to false to prevent
      // search echo when we add the new search id to the url
      shouldSearch.current = false;

      // Re-filter locally when only client-side filters changed; skip the backend query.
      const serverSignature = buildServerSignature(filters, searchTerm, sortOrder);
      if (searchResults?.id && serverSignature === lastServerSignatureRef.current) {
        const raw = rawCompaniesRef.current || [];
        setCompanySearchResults({
          ...searchResults,
          loadedCount: raw.length,
          companies: applyClientSideCompanyFilters(raw, filters),
        });
        return;
      }
      lastServerSignatureRef.current = serverSignature;

      const myEpoch = ++searchEpoch.current;
      setLoadStatus(FetchStatus.FETCHING);
      // If string contains white space (i.e two words) don't add fuzzy (~) character
      const trimmedTerm = searchTerm.trim();
      const addFuzzy = !/\s/g.test(trimmedTerm) && trimmedTerm.length > 0;
      const params = {
        ...defaultSearch.filters,
        ...filters,
        sortOrders: getCompanySortOrders(sortOrder),
        fullTextSearch: addFuzzy ? `${trimmedTerm}~` : trimmedTerm,
        limit: 50,
      };
      createCompanySearch(params)
        .then((res) => {
          if (myEpoch !== searchEpoch.current) return;
          if ('companies' in res) {
            rawCompaniesRef.current = res.companies || [];
            setCompanySearchResults(withClientSideFilters(res));
            setLoadStatus(FetchStatus.SUCCESS);
            // This triggers a location change thus running the location useEffect bollow
            history.push(`/companies?search=${res.id}`);
          }
        })
        .catch(() => {
          if (myEpoch !== searchEpoch.current) return;
          setLoadStatus(FetchStatus.ERROR);
        });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, searchTerm, sortOrder]);

  useEffect(() => {
    // If searchResults.id doesn't equal the id in the query
    // then we can assume the user is navigating url history.
    // If !query?.search we can assume this is just /companies
    // and we shoud exicute an initial search.
    if (searchResults?.id !== query?.search || !query?.search) {
      // if search id exist fetch it
      if (query?.search) {
        shouldSearch.current = false;
        setLoadStatus(FetchStatus.FETCHING);
        const myEpoch = ++searchEpoch.current;
        fetchCompanySearch(query.search)
          .then((res) => {
            if (myEpoch !== searchEpoch.current) return;
            if ('companies' in res) {
              const {
                published,
                firstNameExists,
                photoExists,
                descriptionExists,
                primaryLocations,
                secondaryLocations,
                statuses,
                // TODO(activity-level-removal): drop `activityLevels` destructuring and the
                // legacyActivityLevelsToStatuses fallback below in the cleanup PR after release verification.
                activityLevels,
                fullTextSearch,
                sortOrders,
              } = res;
              const restoredTerm = fullTextSearch?.replace(/~/g, '') || '';
              setSearchTerm(restoredTerm);
              const restoredFilters = {
                published,
                firstNameExists,
                photoExists,
                descriptionExists,
                primaryLocations,
                secondaryLocations,
                statuses: statuses && statuses.length > 0
                  ? statuses
                  : legacyActivityLevelsToStatuses(activityLevels),
              };
              const restoredSort = getCompanySortValue(sortOrders[0]);
              setCompanyFilters(restoredFilters);
              setSortOrder(restoredSort)
              rawCompaniesRef.current = res.companies || [];
              lastServerSignatureRef.current = buildServerSignature(restoredFilters, restoredTerm, restoredSort);
              setCompanySearchResults({
                ...res,
                loadedCount: res.companies?.length ?? 0,
                companies: applyClientSideCompanyFilters(res.companies, restoredFilters),
              });
              setLoadStatus(FetchStatus.SUCCESS);
            }
          })
          .catch(() => {
            if (myEpoch !== searchEpoch.current) return;
            setLoadStatus(FetchStatus.ERROR);
          });
      } else {
        // Laod the default search
        setLoadStatus(FetchStatus.FETCHING);
        shouldSearch.current = false;
        const myEpoch = ++searchEpoch.current;
        const params = {
          ...defaultSearch.filters,
          sortOrders: defaultSearch.sortOrders,
          limit: 50,
        };
        createCompanySearch(params)
          .then((res) => {
            if (myEpoch !== searchEpoch.current) return;
            if ('companies' in res) {
              const {
                published,
                firstNameExists,
                photoExists,
                descriptionExists,
                primaryLocations,
                secondaryLocations,
                statuses,
                // TODO(activity-level-removal): drop `activityLevels` destructuring and the
                // legacyActivityLevelsToStatuses fallback below in the cleanup PR after release verification.
                activityLevels,
                sortOrders,
              } = res;
              const restoredFilters = {
                published,
                firstNameExists,
                photoExists,
                descriptionExists,
                primaryLocations,
                secondaryLocations,
                statuses: statuses && statuses.length > 0
                  ? statuses
                  : legacyActivityLevelsToStatuses(activityLevels),
              };
              const restoredSort = getCompanySortValue(sortOrders[0]);
              setCompanyFilters(restoredFilters);
              setSearchTerm('');
              setSortOrder(restoredSort);
              rawCompaniesRef.current = res.companies || [];
              lastServerSignatureRef.current = buildServerSignature(restoredFilters, '', restoredSort);
              setCompanySearchResults({
                ...res,
                loadedCount: res.companies?.length ?? 0,
                companies: applyClientSideCompanyFilters(res.companies, restoredFilters),
              });
              setLoadStatus(FetchStatus.SUCCESS);
              history.push(`/companies?search=${res.id}`);
            }
          })
          .catch(() => {
            if (myEpoch !== searchEpoch.current) return;
            setLoadStatus(FetchStatus.ERROR);
          });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const handleGetMoreResults = () => {
    if (searchResults?.id && searchResults.offset + 50 < searchResults.companyCount) {
      setLoadMoreStatus(FetchStatus.FETCHING);
      const myEpoch = ++searchEpoch.current;
      fetchCompanySearch(searchResults.id, searchResults.offset + 50)
        .then((res) => {
          if (myEpoch !== searchEpoch.current) return;
          if ('companies' in res) {
            const batch = res.companies || [];
            rawCompaniesRef.current = [...rawCompaniesRef.current, ...batch];
            setCompanySearchResults({
              ...res,
              loadedCount: rawCompaniesRef.current.length,
              companies: [
                ...searchResults.companies,
                ...applyClientSideCompanyFilters(batch, filters),
              ],
            });
            setLoadMoreStatus(FetchStatus.SUCCESS);
          }
        })
        .catch(() => {
          if (myEpoch !== searchEpoch.current) return;
          setLoadMoreStatus(FetchStatus.ERROR);
        });
    }
  };

  useInfiniteScroll(loading, handleGetMoreResults);

  const handleClickReset = () => {
    setSearchTerm('');
    setCompanyFilters(defaultSearch.filters);
    setSortOrder(getCompanySortValue(defaultSearch.sortOrders[0]));
    setLoadMoreStatus(FetchStatus.IDLE);
    setLoadStatus(FetchStatus.IDLE);
  };

  // Used to prevent search term echo on load
  const onSearchChange = (val) => {
    setSearchTerm(val);
    if (val !== searchTerm) {
      shouldSearch.current = true;
      if (sortOrder !== 'relevance') setSortOrder('relevance');
    }
  };

  const onSearchClear = () => {
    setSearchTerm('');
    shouldSearch.current = true;
  };

  const onFilterChange = () => {
    shouldSearch.current = true;
  };

  const handleClickNameSort = () => {
    shouldSearch.current = true;
    if (sortOrder !== 'nameAToZ') {
      setSortOrder('nameAToZ');
    } else {
      setSortOrder('nameZToA');
    }
  };

  const onSortChange = (e) => {
    shouldSearch.current = true;
    setSortOrder(e.target.value);
  };

  return (
    <TripRequestsView
      loadStatus={loadStatus}
      loadMoreStatus={loadMoreStatus}
      offset={searchResults?.offset}
      onChange={onSearchChange}
      onSearchClear={onSearchClear}
      onClickReset={handleClickReset}
      onClickNameSort={handleClickNameSort}
      onFilterChange={onFilterChange}
      searchResults={searchResultsById || {}}
      searchTerm={searchTerm}
      searchTotal={searchResults?.companyCount}
      searchLoadedCount={searchResults?.loadedCount}
      hasClientSideFilters={hasClientSideCompanyFilters(filters)}
      sortOrder={sortOrder}
      onSortChange={onSortChange}
    />
  );
};

export default CompaniesContainer;
